import {
    fetchCampaign,
    fetchCampaignMetadata,
    fetchSupportersCount,
    fetchCampaignNotes,
    assignCampaignCSC,
    fetchCampaignMipBalance,
    refreshCampaignMipBalance,
} from '~/services/campaign';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { useParams } from 'react-router-dom';
import TitlePanel from '~/components/CampaignDetail/TitlePanel';
import PinnedNotes from '~/components/CampaignDetail/PinnedNotes';
import PrimaryTabs from '~/components/CampaignDetail/PrimaryTabs';
import SidebarTabs from '~/components/CampaignDetail/SidebarTabs';
import CscBlock from '~/components/CampaignDetail/blocks/CscBlock';
import FcBlock from '~/components/CampaignDetail/blocks/FcBlock';
import MedicalReasonBlock from '~/components/CampaignDetail/blocks/MedicalReasonBlock';
import FundsRaisedBlock from '~/components/CampaignDetail/blocks/FundsRaisedBlock';
import UniqueDonorsBlock from '~/components/CampaignDetail/blocks/UniqueDonorsBlock';
import { useQueries } from '@tanstack/react-query';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { CampaignContext, CampaignState } from '~/context/CampaignProvider';
import Loader from '~/components/Loader';
import {
    assignCampaignFinancialCoordinator,
    fetchFinancialCoordinators,
} from '~/services/fund-requests';
import { FinancialCoordinatorOption } from '~/types/fundrequests';
import { toast } from 'react-hot-toast';

const CampaignDetail: React.FC = () => {
    const [{ campaignData }, campaignDispatch] =
        useContext<CampaignState | null>(CampaignContext);
    const [financialCoordinators, setFinancialCoordinators] = useState<
        FinancialCoordinatorOption[]
    >([]);
    const [savingFc, setSavingFc] = useState<boolean>(false);
    const [savingCsc, setSavingCsc] = useState<boolean>(false);
    const [isloading, setIsLoading] = useState<boolean>(false);
    const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
    const [mipBalance, setMipBalance] = useState<number | undefined>(undefined);
    const [isRefreshingBalance, setIsRefreshingBalance] = useState<boolean>(false);

    const [allNotes, setAllNotes] = useState<any[]>([]);
    const [pinnedNotes, setPinnedNotes] = useState<any[]>([]);
    const results = useQueries({
        queries: [
            {
                queryKey: ['metadata'],
                queryFn: () => fetchCampaignMetadata(),
                staleTime: Infinity,
            },
        ],
    });

    const metadata = results[0].data;

    const { campaign_id } = useParams();

    const getCampaign = async () => {
        const {
            campaign,
            patient,
            csc,
            campaign_request,
            campaignPhotoInfo,
            coordinator,
            financial_coordinator,
            application,
            approval,
        } = await fetchCampaign(campaign_id);

        let count = 0;

        if (campaign) {
            const supporters = await fetchSupportersCount(campaign.ntaf_id);
            if (supporters) count = supporters.count;
        }

        campaignDispatch({
            type: 'set_campaign',
            payload: {
                campaign,
                patient,
                csc,
                campaign_request,
                campaignPhotoInfo,
                coordinator,
                financial_coordinator,
                count,
                application,
                approval,
            },
        });

        return { campaign, campaign_request };
    };

    // The notes request is fired per campaign and resolves out of band. This
    // route stays mounted when navigating campaign -> campaign, so an in-flight
    // response for the previous campaign would otherwise land on the new one.
    // Every response is checked against the campaign currently on screen and
    // dropped if it no longer matches.
    const notesRequestRef = useRef<string | number | undefined>(undefined);

    const loadNotesAndActivityLog = async (campaignId: number, campaignRequest: any) => {
        // Fetch campaign notes separately (don't block page rendering)
        notesRequestRef.current = campaignId;
        setLoadingNotes(true);
        fetchCampaignNotes(campaignId)
            .then((notesResponse) => {
                if (notesRequestRef.current !== campaignId) return;
                const campaignNotes = notesResponse.results || [];

                // Combine with campaign request notes
                const reqNotes = (campaignRequest?.request_notes || []) as unknown[];
                const combinedNotes = [...(reqNotes || []), ...(campaignNotes || [])] as any[];

                setAllNotes(combinedNotes);
                setPinnedNotes(
                    combinedNotes.filter((note: { pinned_flag: any }) => note.pinned_flag)
                );
            })
            .catch((error) => {
                if (notesRequestRef.current !== campaignId) return;
                console.error('Error fetching notes:', error);
                setAllNotes([]);
                setPinnedNotes([]);
            })
            .finally(() => {
                if (notesRequestRef.current !== campaignId) return;
                setLoadingNotes(false);
            });
    };

    const getFinancialCoordinators = async () => {
        try {
            const resp = await fetchFinancialCoordinators();
            setFinancialCoordinators(resp.results);
        } catch (error) {
            toast.error(error);
            console.log(error);
        }
    };

    const handleSaveFinancialCoordinator = async (
        fc: FinancialCoordinatorOption
    ) => {
        if (!campaign_id) return;
        setSavingFc(true);
        try {
            const resp = await assignCampaignFinancialCoordinator(
                campaign_id,
                fc.id
            );
            console.log('resp: ', resp);
            campaignDispatch({
                type: 'set_financial_coordinator',
                payload: resp,
            });
            toast.success('Financial Coordinator updated successfully');
        } catch (error) {
            console.error('Error assigning FC:', error);
            toast.error('Failed to update Financial Coordinator');
        } finally {
            setSavingFc(false);
        }
    };

    const handleSaveCSC = async (csc: { id: number; full_name: string; first_name?: string; last_name?: string }) => {
        if (!campaign_id) return;
        setSavingCsc(true);
        try {
            const resp = await assignCampaignCSC(campaign_id, csc.id);
            console.log('CSC assignment resp: ', resp);
            campaignDispatch({
                type: 'set_csc',
                payload: resp,
            });
            toast.success('Client Services Coordinator updated successfully');
        } catch (error) {
            console.error('Error assigning CSC:', error);
            toast.error('Failed to update Client Services Coordinator');
        } finally {
            setSavingCsc(false);
        }
    };

    const handleStatusUpdate = (newStatus: { id?: number; code?: string; name?: string }) => {
        campaignDispatch({
            type: 'set_campaign_status',
            payload: newStatus,
        });

    };

    const handlePublishSuccess = (resp: { ID?: number | string; permalink?: string } | null | undefined) => {
        if (!resp?.permalink) return;
        campaignDispatch({
            type: 'set_wordpress_info',
            payload: {
                wordpress_id: resp.ID ?? null,
                wordpress_permalink: resp.permalink,
            },
        });
    };

    const loadSupporterCount = async (ntafId: string) => {
        try {
            const supporters = await fetchSupportersCount(ntafId);
            if (supporters && typeof supporters.count === 'number') {
                campaignDispatch({
                    type: 'set_supporter_count',
                    payload: supporters.count,
                });
            }
        } catch (error) {
            console.error('Error fetching supporter count:', error);
        }
    };

    const loadMipBalance = async (ntafId: string) => {
        try {
            const resp = await fetchCampaignMipBalance(ntafId);
            if (resp?.total !== undefined) {
                setMipBalance(Number(resp.total));
            }
        } catch (error) {
            console.error('Error fetching MIP balance:', error);
        }
    };

    const handleRefreshBalance = async () => {
        const ntafId = campaignData?.campaign?.ntaf_id || campaignData?.campaign?.cf_patient_ntaf_id;
        if (!ntafId) return;

        setIsRefreshingBalance(true);
        try {
            const resp = await refreshCampaignMipBalance(ntafId);
            if (resp?.total !== undefined) {
                setMipBalance(Number(resp.total));
                toast.success('Balance refreshed');
            } else {
                toast.error('Failed to refresh balance from MIP');
            }
        } catch (error) {
            console.error('Error refreshing MIP balance:', error);
            toast.error('Failed to refresh balance');
        } finally {
            setIsRefreshingBalance(false);
        }
    };

    useEffect(() => {
        const existingCampaignId = campaignData?.campaign?.campaign_id?.toString();
        const alreadyLoaded = existingCampaignId === campaign_id;

        // Drop the previous campaign's notes immediately so nothing from it can
        // be shown against this campaign while the new notes load.
        notesRequestRef.current = undefined;
        setAllNotes([]);
        setPinnedNotes([]);
        setLoadingNotes(true);

        const fetchData = async () => {
            try {
                if (alreadyLoaded) {
                    // Context already has this campaign — just load FC options and notes
                    await getFinancialCoordinators();
                    const campaignId = campaignData.campaign?.id || campaignData.campaign?.campaign_id;
                    if (campaignId) {
                        loadNotesAndActivityLog(campaignId, campaignData.campaign_request);
                    } else {
                        setLoadingNotes(false);
                    }
                    const ntafId = campaignData.campaign?.ntaf_id || campaignData.campaign?.cf_patient_ntaf_id;
                    if (ntafId) {
                        loadMipBalance(ntafId);
                        loadSupporterCount(ntafId);
                    }
                } else {
                    setIsLoading(true);
                    const [freshData] = await Promise.all([
                        getCampaign(),
                        getFinancialCoordinators(),
                    ]);
                    setIsLoading(false);

                    const campaignId = freshData.campaign?.id || freshData.campaign?.campaign_id;
                    if (campaignId) {
                        loadNotesAndActivityLog(campaignId, freshData.campaign_request);
                    } else {
                        setLoadingNotes(false);
                    }
                    const ntafId = freshData.campaign?.ntaf_id || freshData.campaign?.cf_patient_ntaf_id;
                    if (ntafId) {
                        loadMipBalance(ntafId);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsLoading(false);
                setLoadingNotes(false);
            }
        };

        fetchData();
    }, [campaign_id]);

    // Update pinnedNotes whenever allNotes changes (e.g., when a note is pinned/unpinned)
    useEffect(() => {
        if (allNotes.length) {
            setPinnedNotes(
                allNotes.filter((note: { pinned_flag?: boolean }) => note.pinned_flag)
            );
        }
    }, [allNotes]);

    if (isloading) return <Loader />;

    const {
        campaign,
        patient,
        csc,
        campaign_request,
        campaignPhotoInfo,
        financial_coordinator,
        count,
        application,
        approval,
    } = campaignData || {};

    return (
        <>
            <Breadcrumb 
                pageName={
                    patient?.person?.first_name && patient?.person?.last_name 
                        ? `${patient.person.first_name} ${patient.person.last_name}` 
                        : 'Campaign Detail'
                }
                parentPage='Campaigns'
                parentPath='/client-campaigns'
            />

            <TitlePanel
                campaign={campaign}
                campaignPhotoInfo={campaignPhotoInfo}
                application={application}
                onStatusUpdate={handleStatusUpdate}
                onRefresh={getCampaign}
                onPublishSuccess={handlePublishSuccess}
            />

            <div className='grid grid-cols-12 gap-7.5'>
                <div className='col-span-12 grid grid-cols-12  gap-7.5'>
                    <div className='col-span-12 grid grid-cols-12 gap-7.5 2xl:col-span-8'>
                        <div className='col-span-12 grid grid-cols-1 gap-7.5 md:grid-cols-3'>
                            <MedicalReasonBlock
                                medicalReason={
                                    patient?.primary_medical_issue?.medical_reason?.name?.replace(
                                        /Catastrophic/,
                                        'Cat'
                                    )
                                }
                                primaryMedicalIssue={
                                    patient?.primary_medical_issue?.name
                                }
                                campaignTypeCode={campaign?.campaign_type?.code}
                                campaignTypeName={campaign?.campaign_type?.name}
                            />
                            <FundsRaisedBlock
                                entireFundsRaised={
                                    campaign?.entire_funds_raised
                                }
                                mipBalance={mipBalance ?? campaign?.mip_balance}
                                onRefreshBalance={campaign?.ntaf_id || campaign?.cf_patient_ntaf_id ? handleRefreshBalance : undefined}
                                isRefreshing={isRefreshingBalance}
                            />
                            <UniqueDonorsBlock
                                id={campaign?.campaign_id}
                                uniqueDonors={count || 0}
                            />
                        </div>
                    </div>
                    <div className='col-span-12 grid grid-cols-12 gap-7.5 2xl:col-span-4'>
                        <div className='col-span-12 grid grid-cols-1 gap-7.5 md:grid-cols-2'>
                            <CscBlock 
                                csc={csc} 
                                campaignId={campaign?.id}
                                handleSaveCSC={handleSaveCSC}
                                saving={savingCsc}
                            />
                            <FcBlock
                                fc={financial_coordinator}
                                financial_coordinators={financialCoordinators}
                                handleSaveFinancialCoordinator={
                                    handleSaveFinancialCoordinator
                                }
                                saving={savingFc}
                            />
                        </div>
                    </div>
                </div>
                <div className='col-span-12 xl:col-span-8'>
                    {!loadingNotes && pinnedNotes.length > 0 && (
                        <div className='mb-7.5'>
                            <PinnedNotes notes={pinnedNotes} setNotes={setAllNotes} />
                        </div>
                    )}
                    <PrimaryTabs
                        campaign={campaign}
                        campaignRequest={campaign_request}
                        patient={patient}
                        metadata={metadata}
                        application={application}
                        approval={approval}
                        csc={csc}
                        onDataUpdate={getCampaign}
                        campaignDispatch={campaignDispatch}
                        onCampaignRequestUpdate={(updatedCampaignRequest) => {
                            // Update the campaign context with new campaign request data
                            campaignDispatch({
                                type: 'update_campaign_request',
                                payload: { campaign_request: updatedCampaignRequest },
                            });
                        }}
                    />
                </div>
                <div className='col-span-12 xl:col-span-4'>
                    <SidebarTabs
                        notes={allNotes}
                        campaign={campaign}
                        campaignRequest={campaign_request}
                        setNotes={setAllNotes}
                        loadingNotes={loadingNotes}
                    />
                </div>
            </div>
        </>
    );
};

export default CampaignDetail;
